import React, { useState } from 'react';
import PageHeader from './PageHeader';
import { VERSIONS, ROADMAP, IDEAS } from '../changelog-data.js';

const TYPE_META = {
  added:     { label: 'Dodane',     icon: '✨', color: '#059669', bg: '#f0fdf4', border: '#bbf7d0' },
  fixed:     { label: 'Naprawione', icon: '🔧', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  changed:   { label: 'Zmienione',  icon: '🔄', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  removed:   { label: 'Usunięte',   icon: '🗑️', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
  security:  { label: 'Bezpieczeństwo', icon: '🔒', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  deprecated:{ label: 'Przestarzałe', icon: '⏳', color: '#92400e', bg: '#fffbeb', border: '#fde68a' },
};

function TypeBlock({ section }) {
  const meta = TYPE_META[section.type] || TYPE_META.changed;
  return (
    <div className="changelog-type-block" style={{ borderColor: meta.border, background: meta.bg }}>
      <h3 style={{ color: meta.color }}>
        <span className="changelog-type-icon">{meta.icon}</span>
        {meta.label}
        <span className="changelog-type-count">{section.items.length}</span>
      </h3>
      <ul>
        {section.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function VersionCard({ v, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const isCurrent = v.status === 'current';

  return (
    <article className={`changelog-version ${isCurrent ? 'is-current' : ''}`}>
      <header className="changelog-version-header" onClick={() => setOpen(o => !o)}>
        <div className="changelog-version-meta">
          <span className={`changelog-version-tag ${isCurrent ? 'tag-current' : 'tag-past'}`}>
            v{v.version}
            {isCurrent && <span className="tag-current-dot" />}
          </span>
          <span className="changelog-version-date">{v.date}</span>
        </div>
        <h2 className="changelog-version-title">{v.title}</h2>
        <div className="changelog-version-summary">
          {v.sections.map(s => {
            const meta = TYPE_META[s.type] || TYPE_META.changed;
            return (
              <span key={s.type} className="changelog-summary-chip" style={{ color: meta.color, background: meta.bg, borderColor: meta.border }}>
                {meta.icon} {s.items.length} {meta.label.toLowerCase()}
              </span>
            );
          })}
          <span className={`changelog-toggle ${open ? 'open' : ''}`}>▼</span>
        </div>
      </header>
      {open && (
        <div className="changelog-version-body">
          {v.sections.map((s, i) => <TypeBlock key={i} section={s} />)}
        </div>
      )}
    </article>
  );
}

function RoadmapCard({ r, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const isNext = r.status === 'next';
  return (
    <article className={`roadmap-card ${isNext ? 'is-next' : ''}`}>
      <header className="roadmap-card-header" onClick={() => setOpen(o => !o)}>
        <span className={`roadmap-tag ${isNext ? 'tag-next' : 'tag-future'}`}>
          v{r.version}
          {isNext && <span className="tag-next-dot">●</span>}
        </span>
        <div className="roadmap-heading">
          <h3>{r.title}</h3>
          <div className="roadmap-meta-row">
            {r.priority && <span className="roadmap-priority">{r.priority}</span>}
            {r.outcome && <span className="roadmap-outcome">{r.outcome}</span>}
          </div>
        </div>
        <span className={`changelog-toggle ${open ? 'open' : ''}`}>▼</span>
      </header>
      {open && (
        <ul className="roadmap-items">
          {r.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
    </article>
  );
}

export default function Changelog() {
  const currentVersion = VERSIONS.find(v => v.status === 'current')?.version || '0.4';
  const stats = {
    released: VERSIONS.length,
    roadmap: ROADMAP.length,
    ideas: IDEAS.length,
  };

  return (
    <section>
      <PageHeader
        icon="📋"
        title="Historia zmian"
        description={`Aktualna wersja: v${currentVersion}. Portal rozwijany iteracyjnie — zobacz, co nowego, co naprawiono i co planujemy.`}
        helpKey="changelog"
        stats={[
          { label: 'Aktualna wersja', value: `v${currentVersion}`, tone: 'success' },
          { label: 'Wydane', value: stats.released, tone: 'info' },
          { label: 'W planach', value: stats.roadmap, tone: 'warning' },
        ]}
      />

      <h2 className="changelog-section-title">📜 Wydane wersje</h2>
      <div className="changelog-list">
        {VERSIONS.map((v, i) => (
          <VersionCard key={v.version} v={v} defaultOpen={i === 0} />
        ))}
      </div>

      <h2 className="changelog-section-title roadmap-title">🚀 Planowane zmiany (Roadmap)</h2>
      <p className="changelog-section-intro">
        Roadmap po przeglądzie: aktualna wersja to v{currentVersion}, więc plan zaczyna się od kolejnego realnego kroku.
        Priorytet: mniej fajerwerków, więcej operacyjnej użyteczności dla Michała i prostoty dla Marii.
      </p>
      <div className="roadmap-list">
        {ROADMAP.map((r, i) => (
          <RoadmapCard key={r.version} r={r} defaultOpen={i === 0} />
        ))}
      </div>

      <h2 className="changelog-section-title ideas-title">💡 Pomysły do rozważenia</h2>
      <p className="changelog-section-intro">
        Nieformalna lista pomysłów. Jeśli coś jest pilnie potrzebne, daj znać — przeniesiemy to do roadmapy.
      </p>
      <ul className="ideas-list">
        {IDEAS.map((idea, i) => (
          <li key={i}>{idea}</li>
        ))}
      </ul>

      <footer className="changelog-footer">
        <p>
          Plik <code>CHANGELOG.md</code> w repozytorium zawiera tę samą listę w formacie markdown.
          Nowe wpisy dodawane są przy każdym wydaniu.
        </p>
      </footer>
    </section>
  );
}
